import { useState, useMemo } from 'react'
import { Mail, Inbox, Send, Paperclip, Search, Plus, Eye, Trash2, Download, X } from 'lucide-react'
import { useEmails, useEmailStats, useDeleteEmail } from '@/hooks/use-api'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { toDisplayDate } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/contexts/UserContext'
import { EmailDetailModal } from '@/components/EmailDetailModal'
import { ComposeEmailModal } from '@/components/ComposeEmailModal'
import { exportEmailsToCSV } from '@/lib/csv-export'
import type { Email } from '@/types'
import { explainError } from '@/lib/explain-error'

export default function Emails() {
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [incoming, setIncoming] = useState<string>('all')
  const [hasAttachment, setHasAttachment] = useState<string>('all')
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [composeModalOpen, setComposeModalOpen] = useState(false)
  const [datePreset, setDatePreset] = useState<
    'all' | 'today' | 'thisWeek' | 'thisMonth' | 'custom'
  >('all')
  const [customFromDate, setCustomFromDate] = useState<string>('')
  const [customToDate, setCustomToDate] = useState<string>('')

  const { toast } = useToast()
  const { isAdmin, impersonatedUser } = useUser()

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))

  // Derive date params from preset and custom dates
  const dateParams = useMemo<{ from_date?: string; to_date?: string }>(() => {
    const fmt = (d: Date) => {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
    const today = new Date()
    switch (datePreset) {
      case 'today':
        return { from_date: fmt(today), to_date: fmt(today) }
      case 'thisWeek': {
        const start = new Date(today)
        start.setDate(today.getDate() - today.getDay())
        const end = new Date(start)
        end.setDate(start.getDate() + 6)
        return { from_date: fmt(start), to_date: fmt(end) }
      }
      case 'thisMonth': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1)
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        return { from_date: fmt(start), to_date: fmt(end) }
      }
      case 'custom':
        return {
          ...(customFromDate && { from_date: customFromDate }),
          ...(customToDate && { to_date: customToDate }),
        }
      case 'all':
      default:
        return {}
    }
  }, [datePreset, customFromDate, customToDate])

  // Build filter params
  const filterParams = {
    search,
    page,
    limit: 25,
    ...(incoming !== 'all' && { incoming: incoming === 'true' }),
    ...(hasAttachment !== 'all' && { has_attachment: hasAttachment === 'true' }),
    ...dateParams,
  }

  const { data, isLoading, error } = useEmails(filterParams)
  const { data: stats } = useEmailStats()
  const deleteMutation = useDeleteEmail()

  const {
    items: emails,
    total,
    currentPage,
    limit: _limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = extract(data)

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this email?')) return

    try {
      await deleteMutation.mutateAsync(id)
      toast({
        title: 'Success',
        description: 'Email deleted successfully.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to delete email'),
        variant: 'destructive',
      })
    }
  }

  const handleViewEmail = (email: Email) => {
    setSelectedEmailId(email.id)
    setDetailModalOpen(true)
  }

  const handleExportCSV = () => {
    // Export the current page of emails
    exportEmailsToCSV(emails)
    toast({
      title: 'Export Successful',
      description: `Exported ${emails.length} emails to CSV`,
    })
  }

  const formatDate = (date: string | undefined) => {
    if (!date) return '-'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const truncateText = (text: string | undefined, maxLength = 60) => {
    if (!text) return '-'
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
  }

  const handleDatePreset = (preset: typeof datePreset) => {
    setDatePreset(preset)
    if (preset !== 'custom') {
      setCustomFromDate('')
      setCustomToDate('')
    }
    firstPage()
  }

  const handleClearDateFilter = () => {
    setDatePreset('all')
    setCustomFromDate('')
    setCustomToDate('')
    firstPage()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="email" />
            <h1 className="text-3xl font-bold tracking-tight">Emails</h1>
          </div>
          <p className="text-muted-foreground mt-1">View and manage your email communications</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" aria-label="Export emails to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button onClick={() => setComposeModalOpen(true)} aria-label="Compose new email">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Compose
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-2 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Total Emails</CardTitle>
              <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">
                {stats.total_emails.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground leading-tight">
                {stats.incoming_emails} incoming, {stats.outgoing_emails} outgoing
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">This Week</CardTitle>
              <Inbox className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">
                {stats.this_week.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground leading-tight">Last 7 days</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">With Attachments</CardTitle>
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">
                {stats.with_attachments.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground leading-tight">
                {((stats.with_attachments / stats.total_emails) * 100).toFixed(1)}% of total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Opened</CardTitle>
              <Send className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">
                {stats.opened_emails.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground leading-tight">
                {((stats.opened_emails / stats.outgoing_emails) * 100 || 0).toFixed(1)}% open rate
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Emails</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Search emails by subject, from, or to..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  firstPage()
                }}
                className="pl-9"
                aria-label="Search emails"
              />
            </div>

            <Select
              value={incoming}
              onValueChange={(value) => {
                setIncoming(value)
                firstPage()
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by direction">
                <SelectValue placeholder="All emails" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Emails</SelectItem>
                <SelectItem value="true">Incoming Only</SelectItem>
                <SelectItem value="false">Outgoing Only</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={hasAttachment}
              onValueChange={(value) => {
                setHasAttachment(value)
                firstPage()
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by attachments">
                <SelectValue placeholder="All emails" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Emails</SelectItem>
                <SelectItem value="true">With Attachments</SelectItem>
                <SelectItem value="false">No Attachments</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date filter bar */}
          <div
            className="flex flex-wrap items-center gap-2 pt-2"
            role="toolbar"
            aria-label="Filter emails by date"
          >
            <span className="text-sm text-muted-foreground mr-1">Date:</span>
            {[
              { value: 'all', label: 'All' },
              { value: 'today', label: 'Today' },
              { value: 'thisWeek', label: 'This Week' },
              { value: 'thisMonth', label: 'This Month' },
              { value: 'custom', label: 'Custom' },
            ].map((filter) => (
              <Button
                key={filter.value}
                variant={datePreset === filter.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleDatePreset(filter.value as typeof datePreset)}
                aria-pressed={datePreset === filter.value}
                aria-label={`Filter by ${filter.label}`}
              >
                {filter.label}
              </Button>
            ))}
            {datePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <DatePicker
                  value={customFromDate}
                  onChange={(v) => {
                    setCustomFromDate(v ?? '')
                    firstPage()
                  }}
                  className="w-36 h-8"
                  aria-label="From date"
                />
                <span className="text-sm text-muted-foreground">to</span>
                <DatePicker
                  value={customToDate}
                  onChange={(v) => {
                    setCustomToDate(v ?? '')
                    firstPage()
                  }}
                  className="w-36 h-8"
                  aria-label="To date"
                />
              </div>
            )}
            {datePreset !== 'all' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearDateFilter}
                aria-label="Clear date filter"
              >
                <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Emails Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Email Messages
            {total > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({total.toLocaleString()} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8" role="status" aria-live="polite">
              <p>Loading emails...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive" role="alert">
              Error loading emails. Please try again.
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {search ? 'No emails found matching your search criteria.' : 'No emails found.'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">Type</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-[50px]">Status</TableHead>
                      <TableHead className="text-right w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emails.map((email) => (
                      <TableRow key={email.id}>
                        <TableCell>
                          {email.incoming ? (
                            <Badge variant="secondary" className="gap-1">
                              <Inbox className="h-3 w-3" aria-hidden="true" />
                              <span className="sr-only">Incoming</span>
                            </Badge>
                          ) : (
                            <Badge variant="default" className="gap-1">
                              <Send className="h-3 w-3" aria-hidden="true" />
                              <span className="sr-only">Outgoing</span>
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="link"
                            className="p-0 h-auto font-medium text-left"
                            onClick={() => handleViewEmail(email)}
                            aria-label={`View email: ${email.subject || 'No subject'}`}
                          >
                            <div className="flex items-center gap-2">
                              {truncateText(email.subject, 50)}
                              {email.has_attachment && (
                                <Paperclip
                                  className="h-3 w-3 text-muted-foreground"
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                          </Button>
                        </TableCell>
                        <TableCell>
                          {truncateText(email.from_address || email.from_name, 30)}
                        </TableCell>
                        <TableCell>{truncateText(email.to_address, 30)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(email.message_date)}
                        </TableCell>
                        <TableCell>
                          {email.is_opened && (
                            <Badge variant="success" className="text-xs">
                              Opened
                            </Badge>
                          )}
                          {email.is_bounced && (
                            <Badge variant="destructive" className="text-xs">
                              Bounced
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewEmail(email)}
                              aria-label={`View email: ${email.subject || 'No subject'}`}
                            >
                              <Eye className="h-4 w-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(email.id)}
                              aria-label={`Delete email: ${email.subject || 'No subject'}`}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t pt-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={prevPage}
                      disabled={!hasPrevPage}
                      aria-label="Previous page"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={nextPage}
                      disabled={!hasNextPage}
                      aria-label="Next page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Modal */}
      <EmailDetailModal
        emailId={selectedEmailId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />

      {/* Compose Email Modal */}
      <ComposeEmailModal open={composeModalOpen} onOpenChange={setComposeModalOpen} />
    </div>
  )
}
